
	import { Component, Prop, Vue, Watch, Emit } from "vue-property-decorator";
	import { Getter, namespace } from "vuex-class";
    import { ActionPlanTask } from "@/store/tasks/types";
    import { stringMixin } from "@/mixins";
	import { EventDetails } from "@/store/site-monitor/types";
    import api from "@/services/api.event.service";
	import { cloneDeep, get, isEqual } from 'lodash';
	import { FeaturesList } from "@/store/types";

    const { isNullOrWhitespace } = stringMixin.methods;
	const SiteMonitor = namespace("siteMonitor");

	interface changePriorityTaskData { 
		description: string | null;
		priority: number | null;
		leaveEventOnCompletion?: boolean;
	}

	@Component({})
    export default class ChangePriorityTask extends Vue {
        
		@Prop({ default: () => ({ taskData: { description: null, priority: null }, taskText: "", completed: false }) }) 
		public task!: ActionPlanTask;
        @Prop({ type: Boolean, default: false }) public editing: Boolean;
		@SiteMonitor.Getter("getEventDetails") private eventDetails: EventDetails;
		@SiteMonitor.Mutation setEventDetails: (details: EventDetails) => void;
		@SiteMonitor.Action private fetchEventDetails: (eventId: number) => Promise<EventDetails>;
		@Getter("getFeaturesList") featuresList: FeaturesList;

        public taskData: changePriorityTaskData = {
            description: null,
			priority: null
		};

		private minimumPriority: number = 0;
		private maximumPriority: number = 999999;

        @Watch("task", { immediate: true, deep: true })
		private onTaskChanged(newValue, oldValue): void {

			if(newValue == null || !newValue.taskData)
			{
				this.taskData = {
					priority: 0,
					description: ""
				};
				return
			}
			else if(!isEqual(newValue, oldValue))
			{
				this.taskData = cloneDeep(this.task.taskData);
			}
			
        }

		public get eventId(): number {
            return this.eventDetails ? this.eventDetails.eventID : 0;
		}

		public get isLeaveEventOnPriorityChangeEnabled(): boolean {
			return !!get(this.featuresList, ["Actions", "ChangePriority", "LeaveEventOnPrioritySet"]);
		}

        @Emit("validate-task")
		private onValidateTask(): boolean {
			if (!this.validateInputs) { 
				return false;
			}
			return true;
		}

		private get validateInputs(): boolean {
			return !(
				this.taskData == null
				|| this.task == null
				|| this.isPriorityInvalid
				|| this.task.taskText == null || isNullOrWhitespace(this.task.taskText)
				|| this.taskData.description == null || isNullOrWhitespace(this.taskData.description)
			);
		}
		private get isPriorityInvalid(): boolean {
			return this.taskData == null
				|| this.taskData.priority == null
				|| this.taskData.priority.toString() === ""
				|| isNaN(this.taskData.priority)
				|| this.taskData.priority <= this.minimumPriority - 1
				|| this.taskData.priority > this.maximumPriority;
		}

		private async onClickChangePriority(): Promise<void> {
			if (this.isPriorityInvalid) {
				return;
			}

			try {
				await api.updateEventQueueItem(this.eventId, null, this.taskData.priority, false);
				const currentEventDetails = await this.fetchEventDetails(this.eventId);
				if (currentEventDetails == null) {
					this.$notify({
						type: "error",
						title: "Change Priority Failed",
						text: "Unable to change priority - could not retrieve event details."
					});
					return;
				}
				const updatedDetails = { ...currentEventDetails, priority: this.taskData.priority || 0 };
				this.setEventDetails(updatedDetails);
				this.emitCompleteTask();
				if (this.isLeaveEventOnPriorityChangeEnabled && this.taskData.leaveEventOnCompletion) {
					this.emitCompleteTaskNowLeaveEvent();
				}
			} catch (error) {
				console.error("Failed to update event priority", error);
			}
		}
		
		private formatCompleteTask(): ActionPlanTask {
			return {
				...this.task,
				taskData: this.taskData || null,
				result: "Priority Changed to: " + this.taskData.priority,
				completed: true
			}
		}

		@Emit("complete-task-set-leave-event")
		public emitCompleteTaskNowLeaveEvent(): ActionPlanTask {
			return this.formatCompleteTask();
		}


		@Emit("complete-task")
		public emitCompleteTask(): ActionPlanTask {
			return this.formatCompleteTask();
		}
		
		@Watch("taskData", { deep: true })
		private taskDataUpdated(): void
		{
			if(!this.editing)
			{
				return;
			}

			if(!this.validateInputs)
			{
				return;
			}

			this.$emit("input", {
				...this.task,
				taskData: this.taskData || null,
				taskText: this.task.taskText || ""
			});
		}
	}
